import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export type TrailLeakageStatusFilter =
  | 'all'
  | 'open'
  | 'investigating'
  | 'resolved'
  | 'false_positive';

export type TrailLeakageAlertStatus =
  | 'open'
  | 'investigating'
  | 'resolved'
  | 'false_positive';

export interface TrailLeakageAlert {
  alertId: string;
  dealId: string;
  adviserId: string | null;
  adviserName: string | null;
  valuttenBrokerId: string | null;
  brokerName: string | null;
  officeName: string | null;
  primaryBorrowerName: string | null;
  lenderName: string | null;
  canonicalLender: string | null;
  totalLoanAmountCents: number | null;
  dateSettled: string | null;
  monthsSinceSettlement: number | null;
  alertStatus: string;
  createdAt: string | null;
  updatedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNotes: string | null;
}

export interface TrailLeakageTotals {
  open: number;
  investigating: number;
  resolved: number;
  falsePositive: number;
  totalSuspectLoanValueCents: number;
}

interface TrailLeakageResponse {
  alerts: TrailLeakageAlert[];
  totals: TrailLeakageTotals;
  lastSyncAt: string | null;
}

interface UseTrailLeakageParams {
  companyId: string | undefined;
  status?: TrailLeakageStatusFilter;
  limit?: number;
}

interface UseTrailLeakageState {
  alerts: TrailLeakageAlert[];
  totals: TrailLeakageTotals;
  lastSyncAt: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /**
   * Transition one alert's status. Optimistically patches local state, then
   * calls updateTrailLeakageAlertStatus and refetches to reconcile. Throws on
   * failure (the page surfaces it and the refetch restores server truth).
   */
  updateStatus: (
    alertId: string,
    newStatus: TrailLeakageAlertStatus,
    resolutionNotes?: string
  ) => Promise<void>;
  /** True while a status mutation is in flight (for disabling row controls). */
  updatingAlertId: string | null;
}

const INITIAL_TOTALS: TrailLeakageTotals = {
  open: 0,
  investigating: 0,
  resolved: 0,
  falsePositive: 0,
  totalSuspectLoanValueCents: 0,
};

export function useTrailLeakage({
  companyId,
  status = 'open',
  limit = 200,
}: UseTrailLeakageParams): UseTrailLeakageState {
  const [alerts, setAlerts] = useState<TrailLeakageAlert[]>([]);
  const [totals, setTotals] = useState<TrailLeakageTotals>(INITIAL_TOTALS);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [updatingAlertId, setUpdatingAlertId] = useState<string | null>(null);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const updateStatus = useCallback(
    async (
      alertId: string,
      newStatus: TrailLeakageAlertStatus,
      resolutionNotes?: string
    ): Promise<void> => {
      if (!companyId) return;
      setUpdatingAlertId(alertId);
      // Optimistic local patch so the row reflects the new status immediately.
      setAlerts((prev) =>
        prev.map((a) => (a.alertId === alertId ? { ...a, alertStatus: newStatus } : a))
      );
      try {
        const fn = httpsCallable<
          {
            companyId: string;
            alertId: string;
            newStatus: TrailLeakageAlertStatus;
            resolutionNotes?: string;
          },
          { ok: true }
        >(functions, 'updateTrailLeakageAlertStatus');
        await fn({ companyId, alertId, newStatus, resolutionNotes });
        // Refetch to reconcile counts/totals and confirm server truth.
        setRefreshKey((k) => k + 1);
      } catch (err) {
        console.error('[useTrailLeakage] updateStatus failed', err);
        // Roll back the optimistic patch by refetching server state.
        setRefreshKey((k) => k + 1);
        throw err instanceof Error ? err : new Error(String(err));
      } finally {
        setUpdatingAlertId(null);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fn = httpsCallable<
      { companyId: string; status: TrailLeakageStatusFilter; limit: number },
      TrailLeakageResponse
    >(functions, 'getTrailLeakageReport');

    setLoading(true);
    setError(null);
    fn({ companyId, status, limit })
      .then((result) => {
        setAlerts(result.data.alerts);
        setTotals(result.data.totals);
        setLastSyncAt(result.data.lastSyncAt);
      })
      .catch((err) => {
        console.error('[useTrailLeakage] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, status, limit, refreshKey]);

  return { alerts, totals, lastSyncAt, loading, error, refresh, updateStatus, updatingAlertId };
}
